import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import type { JSX } from "react";

import { PlatformPill, SectionCardHeader } from "../../shared.js";
import type { PlatformAccount } from "../../types.js";
import { Chip } from "./atoms.js";

/** Platforms readiness — connected social accounts + whether auto-publish is on. */
export function PlatformsCard({
  accounts,
}: {
  accounts: PlatformAccount[] | undefined;
}): JSX.Element {
  return (
    <Card data-testid="status-platforms">
      <SectionCardHeader
        title="Where it can post"
        subtitle="Connected social accounts and whether auto-publishing is enabled for each."
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {!accounts ? (
          <Skeleton variant="rounded" height={64} />
        ) : accounts.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            No social accounts connected yet. Connect them in Settings to let
            the agent publish — until then everything stays in the review queue.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1 }}>
            {accounts.map((a) => {
              const connected = a.status === "connected";
              return (
                <Box
                  key={a.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
                  >
                    <PlatformPill platform={a.platform} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {a.display_name || a.account_name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                    <Chip
                      tint={(t) =>
                        connected ? t.palette.success.main : t.palette.error.main
                      }
                    >
                      {connected ? "Connected" : a.status}
                    </Chip>
                    <Chip
                      tint={(t) =>
                        a.publish_enabled
                          ? t.palette.success.main
                          : t.palette.text.secondary
                      }
                    >
                      {a.publish_enabled ? "Auto-publish" : "Manual"}
                    </Chip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
